import { Component, forwardRef } from '@angular/core';
import { FormGroup, FormControl, Validators, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseFormComponent } from 'src/app/widgets/global/BaseFormComponent';
import { VideoThumbnailService } from 'src/app/services/video-thumbnail.service';
import { PatternValidator } from '../../global/Validators';
import { UrlValidator } from 'src/app/validators/url-validator'

@Component({
  standalone: false,
  selector: 'app-video-form',
  templateUrl: './video-form.component.html',
  styleUrls: ['./video-form.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => VideoFormComponent),
    multi: true
  }]
})
export class VideoFormComponent extends BaseFormComponent {

  videoThumbnailService: VideoThumbnailService;
  vm: Record<string, unknown> = {};
  thumbnailLoading: boolean;

  constructor(videoThumbnailService: VideoThumbnailService, private urlValidator: UrlValidator) {
    super();

    this.videoThumbnailService = videoThumbnailService;
    this.form = new FormGroup({
      videoUrl: new FormControl('',
        [
          PatternValidator.url(),
          this.urlValidator.validateUrl(),
          PatternValidator.pattern(String.raw`((https?://)(www\.)?youtube\.com.+)|((https?://)(www\.)?youtu.be/.+)`, "please provide a valid youtube video address"),
        ]),
      videoCaption: new FormControl('', [
        PatternValidator.noWhiteSpace()
      ]),
    });

    // Subscribe to VideoUrl control's value changes, so we can enable/disable required validator to the caption
    this.form.get('videoUrl').valueChanges.subscribe( (videoUrl: string) => {

      if(videoUrl){
        this.form.get('videoCaption').setValidators([PatternValidator.noWhiteSpace(), Validators.required]);
      }else{

        // If videoUrl is cleared out, then it is required to clear the videoThumbnail as well.
        (this.value).videoThumbnail = "";

        // Update the validators for the video caption and empty the value of the caption, since we don't want to push a video without url but with caption.
        this.form.get('videoCaption').setValue('');
        this.form.get('videoCaption').setValidators([PatternValidator.noWhiteSpace()]);
      }

      this.form.get('videoCaption').updateValueAndValidity();
    });

    this.onValueChanged(p => {
      this.setValues(p.videoUrl as string, p.videoCaption as string);

      this.form.valueChanges.subscribe(values => {
        this.value.videoUrl = values.videoUrl;
        this.value.videoCaption = values.videoCaption;
      });
    });
  }


  async videoUrlChanged(formControl: { value: string } | null) {

    // Set Required validator to the video's caption
    if(formControl){
      const captionControl = this.form.get('videoCaption');

      if(formControl.value){
        captionControl.setValidators([PatternValidator.noWhiteSpace(), Validators.required]);
      }else{
        captionControl.setValidators([PatternValidator.noWhiteSpace()]);
      }
      captionControl.updateValueAndValidity();
    }

    await this.updateThumbnail((formControl && formControl.value) ? formControl.value : null);
  }

  async updateThumbnail(videoUrl: string | null) {
    if (videoUrl) {
      this.thumbnailLoading = true;

      try {
        const thumbnails = await this.videoThumbnailService.generateThumbnail(videoUrl);

        if (thumbnails && thumbnails.length > 0) {
          this.thumbnailLoading = false;
          this.vm.thumbnails = thumbnails;
          (this.value).videoThumbnail = thumbnails[0];
        }
      }
      catch {
        this.thumbnailLoading = false;
        this.vm.thumbnails = null;
      }
      finally {
        this.thumbnailLoading = false;
      }
    }
    else {
      this.vm.thumbnails = null;
    }
  }

  async setValues(videoUrl: string, videoCaption: string): Promise<void> {
    this.form.get('videoUrl').setValue(videoUrl);
    this.form.get('videoCaption').setValue(videoCaption);
    await this.updateThumbnail(videoUrl);
  }
}
